import { AxiosPromise, AxiosResponse } from 'axios';
import { apiService, manageFilters } from '@app/services';
import { IActiveFilter } from '@app/models';

class AgentsService {
    readonly apiBase: string = '/api/directory/AgentProfiles';
    readonly agentBaseUrl: string = '/api/agents/register';

    getAgents(orderBy: string, asc: string, top: number, skip: number, organizationId: string, isReset: boolean, filters?: IActiveFilter[]): AxiosPromise {
        return apiService.get(this.apiBase, {
            params: {
                $orderBy: orderBy + ' ' + asc,
                $top: top,
                $skip: skip,
                $count: isReset,
                ...(filters ? { $filter: manageFilters(filters, organizationId) } : { $filter: manageFilters([], organizationId) })
            }
        })
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getAgentTypes(): AxiosPromise {
        return apiService.get(`api/directory/AgentTypes?$orderby=DisplayName%20asc`)
            .catch((error: AxiosResponse) => Promise.reject(error));
    }

    getAgentProfiles(agentId: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        return apiService.get(`${this.apiBase}(${agentId})`,
            { onError: handleError, onSuccess: handleSuccess })
            .catch();
    }

    getClientDetailsForAgent(agentId: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        return apiService.get(`${this.apiBase}(${agentId})/GetClientDetailsForAgent`,
            { onError: handleError, onSuccess: handleSuccess })
            .catch();
    }

    getAgentDetail(): AxiosPromise {
        return apiService.get(`${this.apiBase}/GetDirSyncAgentProfiles`)
            .catch();
    }

    getAgentProtectedConfig(agentId: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        return apiService.get(`${this.apiBase}(${agentId})/GetAgentProtectedConfig`,
            { onError: handleError, onSuccess: handleSuccess })
            .catch();
    }

    getOpenIdAgentProfiles(): AxiosPromise {
        return apiService.get(`${this.apiBase}/GetOpenIdAgentProfiles`, {
            params: {
                $orderBy: 'AgentName asc',
            }
        })
            .catch();
    }

    deleteAgents(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}/DeleteProfiles`;

        return apiService.post(apiUrl,
            jsonObject,
            { onError: handleError, onSuccess: handleSuccess })
            .catch();
    }

    updateAgent(jsonObject: any, agentId: string, handleError: Function, handleSuccess: Function): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${agentId})`;

        return apiService.patch(apiUrl,
            jsonObject,
            { onError: handleError, onSuccess: handleSuccess })
            .catch();
    }

    updateAgentProtectedConfig(jsonObject: any, agentId: string): AxiosPromise {
        const apiUrl: string = `${this.apiBase}(${agentId})/UpdateAgentProtectedConfig`;

        return apiService.post(apiUrl, jsonObject)
            .catch();
    }

    postAgent(jsonObject: any, handleError: Function, handleSuccess: Function): AxiosPromise {
        return apiService.post(this.apiBase,
            jsonObject,
            {
                onError: handleError,
                onSuccess: handleSuccess
            })
            .catch();
    }

    createDefault(prefix: string, orgId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        switch (prefix) {
            case 'radius':
                return apiService.post(this.agentBaseUrl + '/radiusdefault',
                    { OrganizationId: orgId },
                    { onSuccess: handleSuccess, onError: handleError })
                    .catch();
            case 'cp':
                return apiService.post(this.agentBaseUrl + '/windowslogondefault',
                    { OrganizationId: orgId },
                    { onSuccess: handleSuccess, onError: handleError })
                    .catch();
            default:
                return apiService.post(this.agentBaseUrl + '/webdefault',
                    { Prefix: prefix, OrganizationId: orgId },
                    { onSuccess: handleSuccess, onError: handleError })
                    .catch();
        }
    }

    deleteUnused(agentId: string): AxiosPromise {
        const apiUrl: string = this.agentBaseUrl + `/deleteunused/${agentId}`;

        return apiService.delete(apiUrl)
            .catch();
    }

    getClientPermissions(clientId: string, handleSuccess: Function, handleError: Function) {
        const apiUrl: string = this.apiBase + '/GetClientPermissions';

        return apiService.get(apiUrl, {
            params: {
                clientId: clientId
            },
            onSuccess: handleSuccess,
            onError: handleError
        })
            .catch();
    }

    getMissingClientPermissions(clientId: string,
        orderBy: string,
        asc: string,
        top: number,
        skip: number,
        isReset: boolean,
        filters?: IActiveFilter[]): AxiosPromise {
        const apiUrl: string = this.apiBase + '/MissingClientPermissions';
        return apiService.get(apiUrl, {
            params: {
                clientId: clientId,
                $orderBy: orderBy + ' ' + asc,
                $top: top,
                $skip: skip,
                $count: isReset,
                ...(filters ? { $filter: manageFilters(filters) } : {})
            }
        })
            .catch();
    }

    addClientPermissions(clientId: string, permissions: string[], handleSuccess: Function, handleError: Function): AxiosPromise {
        const apiUrl: string = this.apiBase + '/AddClientPermissions';
        return apiService.post(apiUrl,
            {
                clientId: clientId,
                roleIds: permissions
            },
            {
                onSuccess: handleSuccess,
                onError: handleError
            })
            .catch();
    }

    removeClientPermission(clientId: string, roleId: string,  handleSuccess: Function, handleError: Function): AxiosPromise {
        const apiUrl: string = this.apiBase + '/RemoveClientPermission';

        return apiService.post(apiUrl,
            {
                clientId: clientId,
                roleId: roleId
            },
            {
                onSuccess: handleSuccess,
                onError: handleError
            })
            .catch();
    }

    getPublicAgent(organizationId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        const apiUrl = this.agentBaseUrl + `/public-client/${organizationId}`;

        return apiService
            .get(apiUrl, {onSuccess: handleSuccess, onError: handleError})
            .catch();
    }

    registerPublicAgent(organizationId: string, handleSuccess: Function, handleError: Function): AxiosPromise {
        const apiUrl = this.agentBaseUrl + `/public-client/${organizationId}`;

        return apiService
            .post(apiUrl, null, {onSuccess: handleSuccess, onError: handleError})
            .catch();
    }

    updatePublicAgent(organizationId: string, enabled: boolean, handleSuccess: Function, handleError: Function) {
        const apiUrl = this.agentBaseUrl + `/public-client/${organizationId}`;

        return apiService
            .put(apiUrl, {isActive: enabled}, {onSuccess: handleSuccess, onError: handleError})
            .catch();
    }
}

export const agentsService = new AgentsService();
